import React, { useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getEnglishBookName } from "@/pages/Home";

export default function Search() {
  const { lang, version } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get("/bible/search", {
        params: { translation: version, q: q.trim() },
      });
      setResults(res.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">{version}</p>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {lang === "es" ? "Buscar en la Escritura" : "Search the scriptures"}
      </h1>

      <form onSubmit={doSearch} className="mt-8 flex items-center gap-3 max-w-2xl">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" strokeWidth={1.5} />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("searchPlaceholder", lang)}
            className="pl-11 h-12 rounded-full bg-[var(--surface)] border-[var(--border)]"
          />
        </div>
        <button
          data-testid="search-submit"
          type="submit"
          className="h-12 px-6 rounded-full bg-[var(--text-primary)] text-[var(--bg)] hover:bg-[var(--accent)] hover:text-white transition-colors duration-300 text-sm uppercase tracking-widest"
        >
          {lang === "es" ? "Buscar" : "Search"}
        </button>
      </form>

      {q.trim().length < 3 && !searched && (
        <p className="mt-4 text-xs text-[var(--text-secondary)] uppercase tracking-widest">
          {t("searchHint", lang)}
        </p>
      )}

      <div className="mt-10 max-w-3xl space-y-4">
        {loading && <p className="text-[var(--text-secondary)]">{t("loading", lang)}</p>}
        {!loading && searched && results.length === 0 && (
          <p className="text-[var(--text-secondary)]">{t("noResults", lang)}</p>
        )}
        {results.map((r, idx) => {
          const bookName = r.book_name || getEnglishBookName(r.book);
          return (
            <button
              data-testid={`search-result-${idx}`}
              key={`${r.book}-${r.chapter}-${r.verse}-${idx}`}
              onClick={() => navigate(`/read?b=${r.book}&c=${r.chapter}&v=${r.verse}`)}
              className="w-full text-left border-b border-[var(--border)] pb-4 pt-3 group"
            >
              <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)] mb-1">
                {bookName} {r.chapter}:{r.verse}
              </p>
              <p className="font-serif-body text-lg text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors duration-200">
                {r.text}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
